import { Checkbox, Collapsible, FormLayout, Label, RadioButton, Select, TextField } from "@shopify/polaris";
import styles from "./Standard/Standard.module.css";
import { Dispatch, useContext, useState } from "react";
import { MdOutlineArrowBackIosNew } from "react-icons/md";
import AutoComplete from "../../components/AutoComplete";
import { countryArray } from "../../utilities/constants";
import { AppConfigContext } from "../../context/AppConfigContext";
import { currencyToSymbols, TCurrencyCode } from "../utilities";
import TagsField from "../../components/TagsField";
import useBundleMixpanelHook from "../../hooks/useBundleMixpanelHook";
import { TargetingConfig } from "./Types";
import AndSeparator from "../../components/Separators/AndSeparator";
import Additional from "./CommonFields/Additional";

export const TargetingForm = ({
    targeting,
    dispatchBundleTargeting,
    bundleType = "",
    submit,
    additional = null,
    dispatchBundleConfig,
    bundleConfig,
}: {
    bundleType: string;
    targeting: TargetingConfig;
    additional: any;
    submit: any;
    dispatchBundleConfig: any;
    dispatchBundleTargeting: (arg: object) => void;
    bundleConfig: any;
}) => {
    const {
        hide_native_elements,
        add_to_cart_cta_text,
        start_date,
        end_date,
        bundle_embed_id,
        bundles_design: { default_cta },
        bundles_settings: { bundle_position, custom_selector },
        limit_max_quantity_per_product,
        max_quantity_per_product,
        render_embed_on_visibility,
        is_cta_above,
        show_unit_price,
        use_compare_at_price,
        apply_discount_on_compare_price,
        no_preselect_offered_products,
        prevent_deselect_main_product,
        allow_radio_selection,
        remove_addons_if_main_missing,
        show_prices,
        disable_product_title_link,
        price_display_type,
        new_bundle_position,
        new_custom_selector,
        should_flatten_products,
        products,
        discounted_products,
        show_free_gift_popper,
        hide_out_of_stock_products,
        disable_atc_until_threshold,
        atc_threshold,
        atc_tooltip_text,
        bundle_layout,
    } = additional;

    return (
        <div className={styles.collapsibleFormBox}>
            <Additional
                dispatchBundleConfig={dispatchBundleConfig}
                submit={submit}
                add_to_cart_cta_text={add_to_cart_cta_text}
                start_date={start_date}
                end_date={end_date}
                bundleType={bundleType}
                bundle_embed_id={bundle_embed_id}
                default_cta={default_cta}
                bundle_position={bundle_position}
                custom_selector={custom_selector}
                limit_max_quantity_per_product={limit_max_quantity_per_product}
                max_quantity_per_product={max_quantity_per_product}
                render_embed_on_visibility={render_embed_on_visibility}
                hide_native_elements={hide_native_elements}
                is_cta_above={is_cta_above} // using in product addons
                show_unit_price={show_unit_price}
                use_compare_at_price={use_compare_at_price}
                apply_discount_on_compare_price={apply_discount_on_compare_price}
                allow_radio_selection={allow_radio_selection}
                remove_addons_if_main_missing={remove_addons_if_main_missing}
                show_prices={show_prices}
                disable_product_title_link={disable_product_title_link}
                price_display_type={price_display_type}
                new_bundle_position={new_bundle_position} // using in fbt bundle
                new_custom_selector={new_custom_selector} // using in fbt bundle
                bundleConfig={bundleConfig}
                prevent_deselect_main_product={prevent_deselect_main_product}
                no_preselect_offered_products={no_preselect_offered_products}
                should_flatten_products={should_flatten_products}
                products={products}
                discounted_products={discounted_products}
                show_free_gift_popper={show_free_gift_popper}
                hide_out_of_stock_products={hide_out_of_stock_products}
                disable_atc_until_threshold={disable_atc_until_threshold}
                atc_threshold={atc_threshold}
                atc_tooltip_text={atc_tooltip_text}
                bundle_layout={bundle_layout}
            />

            <CustomerTargetingForm
                targeting={targeting}
                dispatchBundleTargeting={dispatchBundleTargeting}
                bundleType={bundleType}
            />
        </div>
    );
};

const CustomerTargetingForm = ({
    targeting,
    dispatchBundleTargeting,
    bundleType = "",
}: {
    bundleType: string;
    targeting: TargetingConfig;
    dispatchBundleTargeting: Dispatch<TargetingConfig | object>;
}) => {
    const [expand, setExpand] = useState(false);
    const { trackTargeting, trackFormSections } = useBundleMixpanelHook();
    const { currency } = useContext(AppConfigContext);

    // useEffect(() => {
    //     const isAllTargeting =
    //         targeting?.includeCustomers === "all" &&
    //         targeting?.excludeCustomers === "none" &&
    //         targeting?.includeLocations?.length === 0 &&
    //         targeting?.excludeLocations?.length === 0;
    //     setTargetingType(isAllTargeting ? "all" : "specific");
    // }, [targeting]);

    const handleResetTargeting = () => {
        trackTargeting({
            cta: "Customer Targeting",
            targetType: "Display Bundle to",
            value: "All",
            bundleType: bundleType,
        });
        dispatchBundleTargeting({
            includeCustomers: "all",
            excludeCustomers: "none",
            includeCustomerData: "",
            excludeCustomerData: "",
            shopifyMarketsTargeting: "all",
            includeLocations: [],
            excludeLocations: [],
            shopifyMarketsTargetingData: [],
            //newKeys
            targetingType: "all",
        });
    };

    const handleSpecificTargeting = () => {
        trackTargeting({
            cta: "Customer Targeting",
            targetType: "Display Bundle to",
            value: "Specific",
            bundleType: bundleType,
        });
        dispatchBundleTargeting({
            targetingType: "specific",
        });
    };

    return (
        <>
            <div
                onClick={() => {
                    setExpand((ps) => {
                        trackFormSections({
                            cta: "Form Section",
                            ctaType: `Customer Targeting ${ps ? "Collapsed" : "Expanded"}`,
                            bundleType: bundleType,
                        });
                        return !ps;
                    });
                }}
                className={styles.sectionTitleWrapper}
            >
                <div>Customer Targeting</div>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        transform: expand ? "rotate(90deg)" : "rotate(270deg)",
                    }}
                >
                    <MdOutlineArrowBackIosNew size={14} cursor={"pointer"} />
                </div>
            </div>

            <Collapsible
                open={expand}
                transition={{
                    duration: "500ms",
                    timingFunction: "ease-in-out",
                }}
                expandOnPrint
                id="basic-collapsible"
            >
                <FormLayout>
                    <div className={styles.customLabelWrapper}>
                        <Label id="bundle_title">Display Bundle to:</Label>
                    </div>
                    <div className={`${styles.formCol}`}>
                        <RadioButton
                            label={"All Customers"}
                            checked={targeting?.targetingType == "all"}
                            onChange={() => {
                                handleResetTargeting();
                            }}
                        />
                        <RadioButton
                            label={"Specific Customer Segment"}
                            checked={targeting?.targetingType == "specific"}
                            onChange={() => {
                                handleSpecificTargeting();
                            }}
                        />
                        {targeting?.targetingType == "specific" && (
                            <div
                                style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: 2,
                                    width: "80%",
                                    marginLeft: 25,
                                }}
                            >
                                <div>
                                    <Checkbox
                                        label={"Based on customer tags"}
                                        checked={targeting?.customerTagsEnabled}
                                        onChange={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Display Bundle to",
                                                value: "Target customers based on customer tag",
                                                bundleType: bundleType,
                                            });
                                            dispatchBundleTargeting({
                                                customerTagsEnabled: !targeting?.customerTagsEnabled,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <Select
                                            label={"Exclusion/Inclusion"}
                                            labelHidden
                                            options={[
                                                { label: "Include", value: "include" },
                                                { label: "Exclude", value: "exclude" },
                                            ]}
                                            value={targeting?.customerTagType}
                                            onChange={(value) => {
                                                dispatchBundleTargeting({
                                                    customerTagType: value,
                                                });
                                            }}
                                        />
                                        <TagsField
                                            label={"Customers tag"}
                                            bundleType={bundleType}
                                            section={"Display bar to"}
                                            labelHidden
                                            placeholder={"Add tags"}
                                            value={targeting?.customerTags}
                                            onChange={(value) => {
                                                dispatchBundleTargeting({
                                                    customerTags: value,
                                                });
                                            }}
                                            autoComplete="off"
                                        />
                                    </div>
                                </div>
                                <AndSeparator
                                    styles={{ marginBottom: "16px", marginLeft: 26 }}
                                    textStyles={{ fontSize: 12 }}
                                />
                                <div>
                                    <Checkbox
                                        label={"Based on customer countries"}
                                        checked={targeting.customerCountriesEnabled}
                                        onChange={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Display Bundle to",
                                                value:
                                                    "Minimum amount spent in store " + targeting?.includeCustomerData,
                                                bundleType: bundleType,
                                            });
                                            dispatchBundleTargeting({
                                                customerCountriesEnabled: !targeting.customerCountriesEnabled,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <Select
                                            label={"Exclusion/Inclusion"}
                                            labelHidden
                                            options={[
                                                { label: "Include", value: "include" },
                                                { label: "Exclude", value: "exclude" },
                                            ]}
                                            value={targeting?.customerCountryType}
                                            onChange={(value) => {
                                                dispatchBundleTargeting({
                                                    customerCountryType: value,
                                                });
                                            }}
                                        />
                                        <AutoComplete
                                            label={"Based on customer countries"}
                                            labelHidden
                                            optionsArray={countryArray.map((country) => ({
                                                value: country.symbol,
                                                label: country.name,
                                            }))}
                                            selected={targeting?.customerCountries}
                                            onChange={(value) => {
                                                dispatchBundleTargeting({
                                                    customerCountries: value,
                                                });
                                            }}
                                            placeholder="Countries"
                                            listLabel="Countries to include"
                                        />
                                    </div>
                                </div>
                                <AndSeparator
                                    styles={{ marginBottom: "16px", marginLeft: 26 }}
                                    textStyles={{ fontSize: 12 }}
                                />
                                <div>
                                    <Checkbox
                                        label={"Based on minimum lifetime spent"}
                                        checked={targeting.customerSpentEnabled}
                                        onChange={() => {
                                            trackTargeting({
                                                cta: "Customer Targeting",
                                                targetType: "Hide Bundle from",
                                                value:
                                                    "Minimum amount spent in store " + targeting?.excludeCustomerData,
                                                bundleType: bundleType,
                                            });
                                            dispatchBundleTargeting({
                                                customerSpentEnabled: !targeting.customerSpentEnabled,
                                            });
                                        }}
                                    />
                                    <div className={styles.formRow} style={{ gridTemplateColumns: "1fr 3fr" }}>
                                        <Select
                                            label={"Exclusion/Inclusion"}
                                            labelHidden
                                            options={[
                                                { label: "Greater than", value: "greater_than" },
                                                { label: "Less than", value: "less_than" },
                                            ]}
                                            value={targeting?.customerSpentType}
                                            onChange={(value) => {
                                                dispatchBundleTargeting({
                                                    customerSpentType: value,
                                                });
                                            }}
                                        />
                                        <TextField
                                            label=""
                                            labelHidden
                                            placeholder={"00"}
                                            value={targeting?.customerSpent}
                                            type={"number"}
                                            onBlur={(e) => {
                                                trackTargeting({
                                                    cta: "Customer Targeting",
                                                    targetType: "Hide Bundle from",
                                                    value:
                                                        "Minimum amount spent in store " +
                                                            // @ts-expect-error hack
                                                            e?.target.value || "0",
                                                    bundleType: bundleType,
                                                });
                                            }}
                                            onChange={(value) => {
                                                dispatchBundleTargeting({
                                                    customerSpent: value,
                                                });
                                            }}
                                            autoComplete="off"
                                            prefix={currencyToSymbols[currency as TCurrencyCode]}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </FormLayout>
            </Collapsible>
        </>
    );
};

export default TargetingForm;
